import * as React from 'react'
import { Box, Button, Link } from '@mui/material'
import { styled } from '@mui/material/styles'
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion'
import MuiAccordionSummary, {
  AccordionSummaryProps
} from '@mui/material/AccordionSummary'
import MuiAccordionDetails from '@mui/material/AccordionDetails'
import Typography from '@mui/material/Typography'

import * as d3 from 'd3'
import {
  getExpression,
  IVariantDetailClinvarGraph,
  Variation
} from '@/@core/services/app.service'
import { useTranslation } from 'react-i18next'
import { ExternalLinks } from '@/@core/services/api'
import { Column } from '@antv/g2plot'
import { OpenInNew, ProgressStarFourPoints } from 'mdi-material-ui'
import { useMutation } from 'react-query'
import { NObject } from '@/@core/components/filter-variants'

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&:before': {
    display: 'none'
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '36px !important'
  }
}))

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={null} {...props} />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)'
  },
  '& .MuiAccordionSummary-root': {
    minHeight: '36px !important'
  },
  '& .MuiAccordionSummary-content': {
    margin: '0 !important',
    marginLeft: theme.spacing(1)
  }
}))

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)'
}))

const BoTree = React.forwardRef((props: any, ref) => {
  const his = React.useRef<HTMLElement | string>('')
  const plot = React.useRef<Column>()

  //

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    plot.current = new Column(his.current, {
      data: [],
      xField: 'title',
      yField: 'value',
      label: {
        // 可手动配置 label 数据标签位置
        position: 'middle', // 'top', 'bottom', 'middle',
        // 配置样式
        style: {
          fill: '#FFFFFF',
          opacity: 0.6
        },
        content: (originData) => {
          return ''
        }
      },
      yAxis: {
        line: null,
        grid: null
      },
      xAxis: {
        label: {
          autoRotate: true,
          autoEllipsis: true,
          autoHide: true
        },
        line: null,
        grid: null
      },
      columnStyle: {
        radius: [20, 20, 0, 0]
      },
      columnWidthRatio: 0.8,
      autoFit: true
    })
  }, [])

  React.useEffect(() => {
    plot.current?.render()
  }, [])

  React.useImperativeHandle(ref, () => {
    return {
      plot
    }
  })

  return <Box ref={his} height={'220px'}></Box>
})

const GeneExpression = React.forwardRef(
  (
    props: {
      value: any[]
      vatiantInfo?: Variation
      gene: string
    },
    ref
  ) => {
    const { t } = useTranslation()
    const treeRef = React.useRef<any>()
    const [gtcData, setGtcData] = React.useState<any[]>([])
    const [gtexData, setGtexData] = React.useState<any[]>([])
    const [loading, setLoading] = React.useState(true)
    const data = [
      {
        title: t('Gene Expression')
      }
    ]

    // 自动加载 public 目录下的文件
    React.useEffect(() => {
      const parseGTCFile = (content: string) => {
        const lines = content.split('\n')
        const data: any[] = []
        let headers: string[] = []

        lines.forEach((line, index) => {
          // 跳过注释行和空行
          if (line.startsWith('#') || line.trim() === '') return

          // 第一行是版本信息
          if (index < 2) return

          // 第二行是列头
          if (index === 2) {
            headers = line.split('\t')
            return
          }

          // 后续行是数据
          const values = line.split('\t')
          const rowData: NObject = {}

          // 添加组织表达值
          for (let i = 0; i < headers.length; i++) {
            rowData[headers[i]] = values[i]
          }

          data.push(rowData)
        })

        return data
      }
      const loadData = async () => {
        try {
          // 从 public 目录加载 .gtc 文件
          const response = await fetch('/GTEx.gct') // 替换为实际文件名

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const content = await response.text()

          const parsedData = parseGTCFile(content)
          setGtcData(parsedData)
          //
        } catch (err) {
          //
        } finally {
          setLoading(false)
        }
      }
      loadData()
    }, [])

    const getGtexData = useMutation({
      mutationFn: async (gene: string) => await getExpression(gene),
      onSuccess: (data: any[]) => {
        if (data && data.length) {
          setGtexData(data)

          // treeRef.current &&
          //   treeRef.current.plot &&
          //   treeRef.current?.plot.current.changeData(data)
        }
      }
    })

    // React.useEffect(() => {
    //   if (props.gene && typeof props.gene === 'string' && props.gene !== '.') {
    //     getGtexData.mutateAsync(props.gene)
    //   }
    // }, [])

    React.useEffect(() => {
      const gene = props.gene
      if (gtcData && gtcData.length) {
        // const data = gtexData.map((items) => {
        //   const temp = gtcData.find((item) => props.gene === item.Description)
        //   if (temp) {
        //     items.median2 = Number(temp[items.tissueSiteDetailId])
        //   }
        //   return {
        //     ...items
        //   }
        // })
        const data: any[] = []
        const temp = gtcData.find((item) => gene === item.Description)

        if (temp) {
          Object.keys(temp).forEach((key) => {
            if (key !== 'Description' && key !== 'Name') {
              data.push({
                title: t(key),
                value: Number(temp[key])
              })
            }
          })
        }

        //

        treeRef.current &&
          treeRef.current.plot &&
          treeRef.current?.plot.current.changeData(data)
      }
    }, [gtcData, props.gene])

    // if (!props.value.length)
    //   return (
    //     <Box>
    //       {data.map((items) => {
    //         return (
    //           <Accordion key={items.title} expanded={true}>
    //             <AccordionSummary
    //               aria-controls="panel1d-content"
    //               id="panel1d-header"
    //               sx={{
    //                 cursor: 'default !important'
    //               }}
    //             >
    //               <Typography>{items.title}</Typography>
    //             </AccordionSummary>

    //             <AccordionDetails>
    //               <Box textAlign={'right'} py={5}>
    //                 <Button
    //                   endIcon={<OpenInNew />}
    //                   size="small"
    //                   variant="outlined"
    //                   onClick={() => {
    //                     const url = `${ExternalLinks.gtex}/${props.vatiantInfo?.gene}`

    //                     window.open(url)
    //                   }}
    //                 >
    //                   GTEx
    //                 </Button>
    //               </Box>

    //               <Box padding={'10px 5px'}>
    //                 <Typography textAlign={'center'}>
    //                   {t('No Results')}
    //                 </Typography>
    //               </Box>
    //             </AccordionDetails>
    //           </Accordion>
    //         )
    //       })}
    //     </Box>
    //   )

    return (
      <Box>
        {data.map((items) => {
          return (
            <Accordion key={items.title} expanded={true}>
              <AccordionSummary
                aria-controls="panel1d-content"
                id="panel1d-header"
                sx={{
                  cursor: 'default !important'
                }}
              >
                <Typography
                  sx={{
                    '::before': {
                      content: '""',
                      display: 'inline-block',
                      width: 4,
                      height: '1.25rem',
                      bgcolor: 'primary.main',
                      position: 'absolute',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      left: '-10px'
                    },
                    lineHeight: '1.25em',
                    position: 'relative',
                    ml: '10px'
                  }}
                >
                  {items.title}
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Box textAlign={'right'} py={5}>
                  <Button
                    endIcon={<OpenInNew />}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const url = `${ExternalLinks.gtex}/${props.vatiantInfo?.gene}`

                      window.open(url)
                    }}
                  >
                    GTEx
                  </Button>
                </Box>

                {/* <Typography padding={'10px 5px'}>{items.description}</Typography> */}

                <BoTree data={[]} ref={treeRef} />
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Box>
    )
  }
)

export default GeneExpression
